import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils';

const ChartContext = React.createContext(null);

function useChart() {
	const context = React.useContext(ChartContext);
	if (!context) {
		throw new Error('useChart must be used within a <ChartContainer />');
	}
	return context;
}

function ChartContainer({ id, className, children, config, ...props }) {
	const uniqueId = React.useId();
	const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

	return (
		<ChartContext.Provider value={{ config }}>
			<div
				data-slot="chart"
				data-chart={chartId}
				className={cn(
					"[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
					className
				)}
				style={{ minHeight: 200 }}
				{...props}
			>
				<RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
			</div>
		</ChartContext.Provider>
	);
}

function ChartTooltipContent({
	active,
	payload,
	className,
	indicator = 'dot',
	hideLabel = false,
	label,
	labelFormatter,
	formatter,
	color,
	nameKey,
	labelKey,
}) {
	const { config } = useChart();

	if (!active || !payload?.length) {
		return null;
	}

	const tooltipLabel = labelFormatter ? labelFormatter(label, payload) : label;

	return (
		<div
			className={cn(
				'border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl',
				className
			)}
		>
			{!hideLabel && tooltipLabel && (
				<div className="font-medium">{tooltipLabel}</div>
			)}
			<div className="grid gap-1.5">
				{payload.map((item, index) => {
					const key = nameKey ? item.payload[nameKey] : item.name || item.dataKey;
					const itemConfig = config?.[item.dataKey] || config?.[key] || {};
					const indicatorColor = color || item.payload?.fill || item.color;
					const value = formatter
						? formatter(item.value, key, item, index, payload)
						: item.value;

					return (
						<div key={`${key}-${index}`} className="flex items-center gap-2">
							{indicator === 'dot' && (
								<div
									className="size-2.5 shrink-0 rounded-[2px]"
									style={{ backgroundColor: indicatorColor }}
								/>
							)}
							{indicator === 'line' && (
								<div
									className="w-4 shrink-0 border-[1.5px] border-current"
									style={{ borderColor: indicatorColor }}
								/>
							)}
							<span className="text-muted-foreground">
								{itemConfig?.label || key}
							</span>
							<span className="font-mono font-medium ml-auto tabular-nums">
								{typeof value === 'number' ? value.toLocaleString() : value}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

const ChartTooltip = RechartsPrimitive.Tooltip;
const ChartLegend = RechartsPrimitive.Legend;

export {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	ChartLegend,
	useChart,
};
